import {
    Box, styled, Alert, Snackbar, SnackbarCloseReason,
} from '@mui/material';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { lowlight } from 'lowlight/lib/common';
import { useEditor, EditorContent, JSONContent } from '@tiptap/react';
import {
    SyntheticEvent, useEffect, useState,
} from 'react';
import { debounce } from 'debounce';
import { JsonEditorStyles } from './JsonEditorStyles';

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    height: '100%',
    overflow: 'auto',
}));

const JsonContainer = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    height: 'calc(100% - 100px)',
}));

function resolveJsonNode(content: JSONContent[] | undefined): string | undefined {
    return content?.reduce((accum: string, item: JSONContent) => (
        (item.type === 'text') ? item.text || '' : accum
    ), '');
}

function createEditableContent(configuration?: Models.SetsPlayground.SetsConfig | null): string {
    return `<pre><code class="language-json">${JSON.stringify(configuration || { sets: [] }, null, 4)}</code></pre>`;
}

const validateJson = debounce((text: string, setError: (err: string | null) => void) => {
    try {
        JSON.parse(text);
        setError(null);
    } catch (err) {
        setError((err as Error).message);
    }
}, 500);

interface SetsConfigJsonEditorProps {
    config: Models.SetsPlayground.SetsConfig | null;
    // TODO: Add this when ready.
    // eslint-disable-next-line react/no-unused-prop-types
    onConfigChange: (config: Models.SetsPlayground.SetsConfig) => void;
}

export const SetsConfigJsonEditor = ({ config, onConfigChange }: SetsConfigJsonEditorProps): JSX.Element => {
    const [initialConfig] = useState(createEditableContent(config));
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const editor = useEditor({
        extensions: [
            Document,
            Paragraph,
            Text,
            CodeBlockLowlight.configure({
                lowlight,
                defaultLanguage: 'json',
                HTMLAttributes: {
                    class: 'hljs',
                },
            }),
        ],
        content: initialConfig,
        onUpdate: ({ editor: e }) => {
            try {
                const json = e.getJSON();
                const text = json.content?.reduce(
                    (accum, content) => (
                        (content.attrs?.language === 'json') ? resolveJsonNode(content.content) || accum : accum),
                    '',
                );

                if (text) {
                    validateJson(text, setError);
                    const parsedConfig = JSON.parse(text) as Models.SetsPlayground.SetsConfig;

                    onConfigChange(parsedConfig);
                }
            } catch (err) {
                setError((err as Error).message);
            }
        },
        autofocus: false,
    });

    useEffect(() => {
        if (error) {
            setSnackbarOpen(true);
        } else {
            setSnackbarOpen(false);
        }
    }, [error]);

    const handleSnackbarClose = (_event: Event | SyntheticEvent, reason: SnackbarCloseReason): void => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <StyledContainer>
            <JsonContainer>
                <JsonEditorStyles />
                <EditorContent editor={editor} />
            </JsonContainer>
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                autoHideDuration={6000}
                open={snackbarOpen && !!error}
                onClose={handleSnackbarClose}
            >
                <Alert severity="error" onClose={(): void => setSnackbarOpen(false)}>
                    Invalid JSON:
                    {' '}
                    {error}
                </Alert>
            </Snackbar>
        </StyledContainer>
    );
};
